import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ListGroup, Badge, Spinner, Form, CloseButton } from 'react-bootstrap';
import { buscarDocumentos, limpiarResultados } from '../redux/busquedaDocumentosSlice';

/**
 * DocumentMentionInput — textarea con menciones "@documento".
 * Reutilizable en Beneficios, Convenios y (más adelante) el editor Canvas de FAQ.
 *
 * Solo admins activan la mención (el backend además protege /api/documentos/search).
 *
 * Props:
 *  - value                    string    texto controlado
 *  - onChange(texto)          función   cambia el texto
 *  - onDocumentosChange(docs) función   recibe el array de documentos enlazados
 *  - esAdmin                  boolean   opcional; por defecto se lee de state.auth.isAdmin
 *  - placeholder, rows, disabled
 *
 * Requiere el reducer `busquedaDocumentos` registrado en el store.
 */
export default function DocumentMentionInput({
  value = '',
  onChange,
  onDocumentosChange,
  esAdmin,
  placeholder = 'Escribe… usa @ para enlazar un documento',
  rows = 4,
  disabled = false,
}) {
  const dispatch = useDispatch();
  const { resultados, cargando } = useSelector((s) => s.busquedaDocumentos);
  const isAdminStore = useSelector((s) => s.auth?.isAdmin);
  const esAdminFinal = esAdmin ?? isAdminStore;

  const textareaRef = useRef(null);
  const debounceRef = useRef(null);
  const peticionRef = useRef(null); // promesa RTK en curso (para abortar la anterior)

  const [abierto, setAbierto] = useState(false);
  const [indiceActivo, setIndiceActivo] = useState(0);
  const [rangoMencion, setRangoMencion] = useState(null); // { start, end, query }
  const [seleccionados, setSeleccionados] = useState([]);

  const listboxId = 'doc-mention-listbox';

  // Detecta un token "@query" pegado al caret (sin espacios). Soporta acentos.
  const detectarMencion = useCallback((texto, caret) => {
    const antes = texto.slice(0, caret);
    const match = antes.match(/(?:^|\s)@([\p{L}\p{N}._-]*)$/u);
    if (!match) return null;
    const query = match[1];
    return { start: caret - query.length - 1, end: caret, query }; // start incluye la @
  }, []);

  // Búsqueda con debounce (~300ms) e ignorando consultas < 2 caracteres.
  const dispararBusqueda = useCallback(
    (query) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (query.length < 2) {
        peticionRef.current?.abort?.();
        dispatch(limpiarResultados());
        return;
      }
      debounceRef.current = setTimeout(() => {
        peticionRef.current?.abort?.(); // cancela la búsqueda anterior en vuelo
        peticionRef.current = dispatch(buscarDocumentos(query));
      }, 300);
    },
    [dispatch]
  );

  const handleChange = (e) => {
    const texto = e.target.value;
    onChange?.(texto);
    if (!esAdminFinal) return; // no-admins: textarea plano, sin mención
    const mencion = detectarMencion(texto, e.target.selectionStart);
    if (mencion) {
      setRangoMencion(mencion);
      setAbierto(true);
      setIndiceActivo(0);
      dispararBusqueda(mencion.query);
    } else {
      setAbierto(false);
      setRangoMencion(null);
    }
  };

  const seleccionarDocumento = useCallback(
    (doc) => {
      if (!doc || !rangoMencion) return;
      // Reemplaza "@query" por "@Titulo " en el texto.
      const nuevoTexto =
        value.slice(0, rangoMencion.start) + `@${doc.titulo} ` + value.slice(rangoMencion.end);
      onChange?.(nuevoTexto);

      // Acumula el documento enlazado sin duplicar.
      setSeleccionados((prev) => {
        if (prev.some((d) => d.id === doc.id)) return prev;
        const next = [...prev, doc];
        onDocumentosChange?.(next);
        return next;
      });

      setAbierto(false);
      setRangoMencion(null);
      dispatch(limpiarResultados());
      requestAnimationFrame(() => textareaRef.current?.focus());
    },
    [rangoMencion, value, onChange, onDocumentosChange, dispatch]
  );

  const quitarDocumento = (id) => {
    setSeleccionados((prev) => {
      const next = prev.filter((d) => d.id !== id);
      onDocumentosChange?.(next);
      return next;
    });
  };

  const handleKeyDown = (e) => {
    if (!abierto || resultados.length === 0) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setIndiceActivo((i) => (i + 1) % resultados.length);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setIndiceActivo((i) => (i - 1 + resultados.length) % resultados.length);
        break;
      case 'Enter':
        e.preventDefault();
        seleccionarDocumento(resultados[indiceActivo]);
        break;
      case 'Escape':
        e.preventDefault();
        setAbierto(false);
        setRangoMencion(null);
        break;
      default:
        break;
    }
  };

  // Limpia el debounce y cancela cualquier búsqueda en vuelo al desmontar.
  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      peticionRef.current?.abort?.();
    },
    []
  );

  const opcionActivaId =
    abierto && resultados[indiceActivo] ? `doc-opt-${resultados[indiceActivo].id}` : undefined;

  return (
    <div className="position-relative">
      <Form.Control
        as="textarea"
        ref={textareaRef}
        rows={rows}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={() => setTimeout(() => setAbierto(false), 150)} // deja pasar el click en la lista
        role="combobox"
        aria-expanded={abierto}
        aria-controls={listboxId}
        aria-autocomplete="list"
        aria-activedescendant={opcionActivaId}
      />

      {abierto && (
        <ListGroup
          id={listboxId}
          role="listbox"
          aria-label="Documentos sugeridos"
          className="position-absolute w-100 shadow-sm"
          style={{ zIndex: 1056, maxHeight: 260, overflowY: 'auto' }}
        >
          {cargando && (
            <ListGroup.Item className="d-flex align-items-center gap-2 text-muted">
              <Spinner animation="border" size="sm" /> Buscando…
            </ListGroup.Item>
          )}

          {!cargando && resultados.length === 0 && (
            <ListGroup.Item className="text-muted">Sin coincidencias</ListGroup.Item>
          )}

          {!cargando &&
            resultados.map((doc, i) => (
              <ListGroup.Item
                key={doc.id}
                id={`doc-opt-${doc.id}`}
                role="option"
                aria-selected={i === indiceActivo}
                active={i === indiceActivo}
                action
                // onMouseDown (no onClick) para no perder el foco antes de seleccionar.
                onMouseDown={(e) => {
                  e.preventDefault();
                  seleccionarDocumento(doc);
                }}
                onMouseEnter={() => setIndiceActivo(i)}
                className="text-truncate"
                title={doc.titulo}
              >
                <i className="bi bi-file-earmark-text me-2" aria-hidden="true"></i>
                {doc.titulo}
              </ListGroup.Item>
            ))}
        </ListGroup>
      )}

      {seleccionados.length > 0 && (
        <div className="d-flex flex-wrap gap-2 mt-2">
          {seleccionados.map((doc) => (
            <Badge
              key={doc.id}
              bg="light"
              text="dark"
              className="d-flex align-items-center gap-1 border"
            >
              <i className="bi bi-paperclip" aria-hidden="true"></i>
              {doc.titulo}
              <CloseButton
                style={{ fontSize: '0.6rem' }}
                onClick={() => quitarDocumento(doc.id)}
                aria-label={`Quitar ${doc.titulo}`}
              />
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}